import { useEffect, useRef } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

/**
 * useHeartbeat - Sends a heartbeat every 60 seconds to track active users.
 * Also sends an offline signal when the user leaves.
 * 
 * Usage: useHeartbeat(user?.email, 'workspace')
 */
export default function useHeartbeat(email, page = 'unknown') {
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!email) return

    const sendHeartbeat = async () => {
      try {
        await fetch(`${API_URL}/activity/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, page })
        })
      } catch (err) {
        // Silent fail — don't break the app if heartbeat fails
      }
    }

    const sendOffline = () => {
      // Use sendBeacon for reliability on page close
      const data = JSON.stringify({ email })
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_URL}/activity/offline`, new Blob([data], { type: 'application/json' }))
      } else {
        fetch(`${API_URL}/activity/offline`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: data,
          keepalive: true
        }).catch(() => {})
      }
    }

    // Send immediately on mount
    sendHeartbeat()

    // Then every 60 seconds
    intervalRef.current = setInterval(sendHeartbeat, 60000)

    // Send offline on page close
    window.addEventListener('beforeunload', sendOffline)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        sendOffline()
      } else if (document.visibilityState === 'visible') {
        sendHeartbeat()
      }
    })

    return () => {
      clearInterval(intervalRef.current)
      window.removeEventListener('beforeunload', sendOffline)
      sendOffline()
    }
  }, [email, page])
}